import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialogRef } from '@angular/material';
import {
  TdLoadingService,
  TdDialogService,
  TdFileUploadComponent
} from '@covalent/core';
import { Subscription } from 'rxjs/Subscription';

import { Category } from '../../shared';
import { IdeaService } from '../../services/idea.service';
import { AuthService } from '../../services/auth.service';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../../environments/environment';

enum FileType {
  ImageFile = 1,
  OptionalFile = 2
}

@Component({
  selector: 'app-idea-create-dialog',
  templateUrl: './idea-create-dialog.component.html',
  styleUrls: ['./idea-create-dialog.component.scss']
})
export class IdeaCreateDialogComponent implements OnInit, OnDestroy {
  /**
   * The form group
   */
  form: FormGroup;

  /**
   * Session data
   */
  sessionData: { [key: string]: any };

  /**
   * List of categories
   */
  categories: Category[] = [];

  /**
   * Subscriptions
   */
  private _subs: Set<Subscription> = new Set();

  /**
   * Error keyword/message map
   */
  static readonly errorMap: {
    [errorKey: string]: string;
  } = {};

  /**
   * File type
   */
  readonly FileType: typeof FileType = FileType;

  /**
   * Constructor
   *
   * @param _fb
   * @param _router
   * @param _activatedRoute
   * @param _dialogRef
   * @param _viewContainerRef
   * @param _loadingService
   * @param _dialogService
   * @param _ideaService
   * @param _authService
   */
  constructor(
    private _fb: FormBuilder,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _dialogRef: MatDialogRef<IdeaCreateDialogComponent>,
    private _viewContainerRef: ViewContainerRef,
    private _loadingService: TdLoadingService,
    private _dialogService: TdDialogService,
    private _ideaService: IdeaService,
    private _authService: AuthService
  ) {
    this.form = _fb.group({
      title: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(100)])
      ],
      description: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(10000)])
      ],
      categoryId: [null, Validators.required],
      imageFile: [null],
      optionalFile: [null]
    });
  }

  /**
   * Init
   */
  ngOnInit() {
    this._subs.add(
      this._authService.sessionData.subscribe((sessionData) => {
        this.sessionData = sessionData;
        this._loadingService.register();
        this._ideaService
          .categorySearch()
          .finally(() => {
            this._loadingService.resolve();
          })
          .subscribe((res: any) => {
            if (res['errors']) {
              // console.log(res);
            } else if (res['result'].data) {
              this.categories = res['result'].data;
            }
          });
      })
    );
  }

  /**
   * Destroy
   */
  ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Returns a list of mimes for image file input
   * The result is a string separated by comma
   */
  get imageFileMimes() {
    return ['image/jpeg', 'image/x-png'].join(',');
  }

  /**
   * Returns a list of mimes for optional file input
   * The result is a string separated by comma
   */
  get optionalFileMimes() {
    return [
      'application/msword',
      'application/vnd.ms-excel',
      'application/vnd.ms-powerpoint',
      'application/pdf',
      'image/jpeg',
      'image/x-png'
    ].join(',');
  }

  /**
   * Opens alert dialog
   *
   * @param message
   * @param title
   */
  private _openAlert(message: string, title: string = '') {
    return this._dialogService.openAlert({
      message: message,
      viewContainerRef: this._viewContainerRef, //OPTIONAL
      title: title, //OPTIONAL, hides if not provided
      closeButton: 'დახურვა', //OPTIONAL, defaults to 'CLOSE'
      width: '400px' //OPTIONAL, defaults to 400px
    });
  }

  /**
   * Returns allowed file size for given file type
   *
   * @param fileType
   */
  private _fileTypeAllowedFileSize(fileType: FileType) {
    return { [FileType.ImageFile]: 5242880, [FileType.OptionalFile]: 26214400 }[
      fileType
    ];
  }

  /**
   * Returns form control name for given file type
   *
   * @param fileType
   */
  private _fileTypeFormControlName(fileType: FileType) {
    return {
      [FileType.ImageFile]: 'imageFile',
      [FileType.OptionalFile]: 'optionalFile'
    }[fileType];
  }

  /**
   * Closes dialog
   */
  close() {}

  /**
   *
   * @param file
   * @param fileType
   * @param fileInput
   */
  selectEvent(
    file: File,
    fileType: FileType,
    fileInput: TdFileUploadComponent
  ) {
    const allowedFileSize = this._fileTypeAllowedFileSize(fileType),
      formControlName = this._fileTypeFormControlName(fileType);

    if (file.size > allowedFileSize) {
      return this._openAlert('არჩეული ფაილის ზომა აღემატება დასაშვებ ზღვარს.')
        .afterClosed()
        .subscribe(() => fileInput.cancel());
    }

    this.form.controls[formControlName].setValue(file);
  }

  /**
   * File cancellation event
   *
   * @param fileType
   */
  cancelEvent(fileType: FileType) {
    const formControlName = this._fileTypeFormControlName(fileType);
    this.form.controls[formControlName].setValue(null);
  }

  /**
   * Add idea
   *
   * @param event
   */
  add(event) {
    if (this.form.valid) {
      const fileControls = [],
        formData = new FormData();

      // Image File
      if (this.form.controls['imageFile'].value) {
        fileControls.push({
          key: 'imageFile',
          value: this.form.controls['imageFile'].value
        });
      }

      // Optional File
      if (this.form.controls['optionalFile'].value) {
        fileControls.push({
          key: 'optionalFile',
          value: this.form.controls['optionalFile'].value
        });
      }

      fileControls.forEach((control) =>
        formData.append('files[]', control.value)
      );

      this._loadingService.register();
      this._ideaService
        .upload(formData)
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe((res: any) => {
          if (res.result['errors']) {
            // console.log(res);
          } else {
            const data = Object.assign({}, this.form.value);

            fileControls.forEach((control, k) => {
              data[`${control.key}Id`] = res['result'].data[k];
            });

            this._loadingService.register();
            this._ideaService
              .add(data)
              .finally(() => {
                this._loadingService.resolve();
              })
              .subscribe((res: any) => {
                if (res.result['errors']) {
                  const errorKey = res.result['errors'][0].keyword.replace(
                      /___ERROR_NOT_FOUND$/,
                      ''
                    ),
                    errorMessage =
                      IdeaCreateDialogComponent.errorMap[errorKey] || errorKey;
                  this._openAlert(errorMessage);
                } else if (res['result'].data) {
                  this._openAlert(
                    'გმადლობთ მონაწილეობისთვის, თქვენი იდეა დარეგისტრირდა.'
                  )
                    .afterClosed()
                    .subscribe(() => {
                      this._dialogRef.close();
                      this._router.navigate([
                        '/idea-details',
                        res['result'].data[0].id
                      ]);
                    });
                }
              });
          }
        });
    }
  }
  get authUrl(): string {
    this._dialogRef.close();
    const isAdmin = this._router.url.includes('admin');
    const urlEndPoint = isAdmin ? 'private' : 'public';
    return `${this.ssoUrl}/${urlEndPoint}/auth/login?redirectUrl=${document.location.href}`;
  }
  get ssoUrl(): string {
    return environment.ssoUrl;
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/components/idea-create-dialog/idea-create-dialog.component.ts